import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Users, Activity } from 'lucide-react';
import { useClientsAndInvitations } from './hooks/useClientsAndInvitations';
import { SendAssessmentModal } from './components/longevity/SendAssessmentModal';
import { useReferralLink } from './hooks/useReferralLink';
import { resendInvite, type ResendInviteResult } from '@/features/authentication/api/inviteApi';
import type { ClientOrInvitation } from './types';
import { ConnectedClientsList } from './components/ConnectedClientsList';
import { PendingInvitationsList } from './components/PendingInvitationsList';
import { ReferralLinkPanel } from '@/shared/components/referral/ReferralLinkPanel';
import { useLongevityFlag } from './longevityFlag';

type MyClientsSectionProps = {
  onInviteClick?: () => void;
};

/**
 * Calculate time remaining until resend is available
 */
const getTimeRemaining = (resendAvailableAt: string | null): number => {
  if (!resendAvailableAt) return 0;
  const availableTime = new Date(resendAvailableAt).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((availableTime - now) / 1000));
};

export function MyClientsSection({ onInviteClick }: MyClientsSectionProps) {
  const queryClient = useQueryClient();
  const { data: clientsAndInvitations, isLoading, isError } = useClientsAndInvitations();
  const {
    data: referralData,
    isLoading: isLoadingReferralLink,
    isError: isReferralLinkError,
  } = useReferralLink();
  const referralLink = referralData?.referralLink;
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sendAssessmentOpen, setSendAssessmentOpen] = useState(false);
  const cooldownsInitialized = useRef(false);

  // Pre-launch gate: the send button and its modal follow the same rule as the
  // tracking section on the Dashboard (see useLongevityFlag).
  const showLongevity = useLongevityFlag();

  // Memoize the filtered arrays to prevent unnecessary re-renders
  const connectedClients = useMemo(
    () =>
      clientsAndInvitations?.filter(
        (item): item is Extract<ClientOrInvitation, { type: 'CONNECTED' }> =>
          item.type === 'CONNECTED'
      ) || [],
    [clientsAndInvitations]
  );
  const pendingInvitations = useMemo(
    () =>
      clientsAndInvitations?.filter(
        (item): item is Extract<ClientOrInvitation, { type: 'PENDING' }> => item.type === 'PENDING'
      ) || [],
    [clientsAndInvitations]
  );

  const connectedCount = connectedClients.length;
  const pendingCount = pendingInvitations.length;
  const hasClients = connectedCount > 0;
  const hasPending = pendingCount > 0;
  const hasAny = hasClients || hasPending;

  // Initialize cooldowns from API response (only once when data loads)
  useEffect(() => {
    if (!pendingInvitations.length || cooldownsInitialized.current) return;

    const newCooldowns: Record<string, number> = {};
    let hasAnyCooldown = false;
    pendingInvitations.forEach((inv) => {
      if (inv.type === 'PENDING' && !inv.canResend && inv.resendAvailableAt) {
        const remaining = getTimeRemaining(inv.resendAvailableAt);
        if (remaining > 0) {
          newCooldowns[inv.id] = remaining;
          hasAnyCooldown = true;
        }
      }
    });
    if (hasAnyCooldown) {
      setCooldowns(newCooldowns);
    }
    cooldownsInitialized.current = true;
  }, [pendingInvitations]);

  useEffect(() => {
    cooldownsInitialized.current = false;
  }, [clientsAndInvitations]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCooldowns((prev) => {
        const hasActiveCooldowns = Object.values(prev).some((t) => t > 0);
        if (!hasActiveCooldowns) return prev;

        const updated: Record<string, number> = {};
        let hasChanges = false;

        Object.entries(prev).forEach(([id, time]) => {
          if (time > 0) {
            updated[id] = time - 1;
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []); // Empty dependency - timer runs independently

  // Resend mutation
  const resendMutation = useMutation({
    mutationFn: resendInvite,
    onMutate: (inviteId) => {
      setResendingId(inviteId);
    },
    onSuccess: (result: ResendInviteResult, inviteId: string) => {
      if (result.success) {
        // Set 30-minute cooldown
        setCooldowns((prev) => ({ ...prev, [inviteId]: 30 * 60 }));
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['advisor', 'invitations'] });
      } else {
        // Set cooldown from error response
        setCooldowns((prev) => ({
          ...prev,
          [inviteId]: result.cooldown.retryAfter,
        }));
      }
    },
    onSettled: () => {
      setResendingId(null);
    },
  });

  const handleResend = useCallback(
    (inviteId: string) => {
      resendMutation.mutate(inviteId);
    },
    [resendMutation]
  );

  const handleCopyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy referral link:', error);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Clients</h3>
        </div>
        <div className="text-center py-8 text-gray-500">Loading clients...</div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Clients</h3>
        </div>
        <div className="text-center py-8 text-red-600">
          Error loading clients. Please try again later.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header with Title and Invite Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg text-gray-900">My Clients</h3>
          {hasAny && (
            <p className="text-sm text-gray-500 mt-1">
              {connectedCount > 0 &&
                `${connectedCount} ${connectedCount === 1 ? 'client' : 'clients'}`}
              {connectedCount > 0 && pendingCount > 0 && ', '}
              {pendingCount > 0 &&
                `${pendingCount} pending ${pendingCount === 1 ? 'invitation' : 'invitations'}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showLongevity && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSendAssessmentOpen(true)}
              className="gap-2"
            >
              <Activity className="w-4 h-4" />
              Send Longevity Readiness Assessment
            </Button>
          )}
          {hasAny && onInviteClick && (
            <Button variant="outline" size="sm" onClick={onInviteClick} className="gap-2">
              <Users className="w-4 h-4" />
              Invite More
            </Button>
          )}
        </div>
      </div>

      <ReferralLinkPanel
        referralLink={referralLink}
        isLoading={isLoadingReferralLink}
        isError={isReferralLinkError}
        copied={copied}
        onCopy={handleCopyReferralLink}
        errorMessage="Unable to load your referral link. Please try again later."
        emptyMessage="Your referral link is being set up. Please check back shortly."
      />

      {!hasAny ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-gray-900 mb-2">No clients yet</h4>
          <p className="text-sm text-gray-500 mb-6">
            Start building your client base by inviting clients to join bQuest
          </p>
          {onInviteClick && (
            <Button onClick={onInviteClick} className="bg-[#026857] hover:bg-[#025247] gap-2">
              <Users className="w-4 h-4" />
              Invite Clients
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connected Clients Section */}
          {hasClients && <ConnectedClientsList clients={connectedClients} />}

          {/* Pending Invitations Section */}
          {hasPending && (
            <PendingInvitationsList
              invitations={pendingInvitations}
              cooldowns={cooldowns}
              resendingId={resendingId}
              onResend={handleResend}
            />
          )}
        </div>
      )}

      {showLongevity && (
        <SendAssessmentModal open={sendAssessmentOpen} onOpenChange={setSendAssessmentOpen} />
      )}
    </Card>
  );
}
